import React from 'react';
import { EffectParams, effects } from '../lib/effects';
import { Section } from './controls/Section';
import { Slider } from './controls/Slider';
import { Switch } from './controls/Switch';
import { ColorPicker } from './controls/ColorPicker';
import { RotateCcw, Play, Pause } from 'lucide-react';
interface SidebarProps {
  activeEffectId: string;
  onEffectChange: (id: string) => void;
  params: EffectParams;
  onParamsChange: (params: EffectParams) => void;
}
export const defaultParams: EffectParams = {
  speed: 1.0,
  intensity: 50,
  color: '#6366f1',
  bgColor: 'transparent',
  playing: true
};
export function Sidebar({
  activeEffectId,
  onEffectChange,
  params,
  onParamsChange
}: SidebarProps) {
  const update = (key: keyof EffectParams, value: any) => {
    onParamsChange({
      ...params,
      [key]: value
    });
  };
  const activeEffect = effects.find(e => e.id === activeEffectId);
  return <div className="absolute top-20 right-4 bottom-4 z-30 w-80 max-w-[calc(100%-2rem)] bg-white/80 backdrop-blur-xl ring-1 ring-slate-900/5 shadow-2xl shadow-slate-900/10 rounded-2xl flex flex-col overflow-hidden" data-id="element-4">
      <div className="px-4 py-3.5 border-b border-slate-900/5 flex justify-between items-center shrink-0" data-id="element-5">
        <h2 className="font-semibold text-slate-800 text-sm" data-id="element-6">Effect Library</h2>
        <button onClick={() => {
        onEffectChange('none');
        onParamsChange(defaultParams);
      }} className="text-xs flex items-center gap-1 text-slate-500 hover:text-indigo-600 transition-colors" data-id="element-7">
          <RotateCcw className="w-3 h-3" data-id="element-8" />
          Reset
        </button>
      </div>

      <div className="flex-1 overflow-y-auto" data-id="element-9">
        <div className="p-4 border-b border-slate-900/5" data-id="element-10">
          <div className="grid grid-cols-2 gap-2" data-id="element-11">
            {effects.map(effect => <button key={effect.id} onClick={() => onEffectChange(effect.id)} className={`px-3 py-2.5 text-sm font-medium rounded-lg border text-left transition-all ${activeEffectId === effect.id ? 'bg-indigo-50 border-indigo-500 text-indigo-700 shadow-sm' : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300'}`} data-id="element-12">
                {effect.name}
              </button>)}
          </div>
        </div>

        <Section title="Adjust" defaultOpen={true} data-id="element-13">
          <div className="flex items-center justify-between mb-2" data-id="element-14">
            <span className="text-xs font-medium text-slate-700" data-id="element-15">Playback</span>
            <button onClick={() => update('playing', !params.playing)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium transition-colors" data-id="element-16">
              {params.playing ? <Pause className="w-3.5 h-3.5" data-id="element-17" /> : <Play className="w-3.5 h-3.5" data-id="element-18" />}
              {params.playing ? 'Pause' : 'Play'}
            </button>
          </div>

          <Slider label="Speed" min={0.1} max={3.0} step={0.1} value={params.speed} onChange={v => update('speed', v)} data-id="element-19" />

          <Slider label="Intensity" min={0} max={100} step={1} value={params.intensity} onChange={v => update('intensity', v)} data-id="element-20" />

          {activeEffect?.usesColor && <>
              <div className="h-px bg-slate-100 my-2" data-id="element-21" />
              <ColorPicker label="Effect Color" value={params.color} onChange={v => update('color', v)} data-id="element-22" />
            </>}

          <div className="h-px bg-slate-100 my-2" data-id="element-23" />
          <ColorPicker label="Background" value={params.bgColor} allowTransparent onChange={v => update('bgColor', v)} data-id="element-24" />
        </Section>
      </div>
    </div>;
}